import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, 
  X, 
  FileText, 
  File, 
  Image, 
  Video,
  Music,
  Archive,
  Trash2,
  Tag,
  Save,
  Crown,
  AlertTriangle
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../context/AuthContext';

interface UploadFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  tags: string[];
  description: string;
}

const DocumentUpload: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { user, canUploadFile, addStorageUsage } = useAuth();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <Image className="w-8 h-8" style={{ color: colors.green }} />;
    if (type.startsWith('video/')) return <Video className="w-8 h-8" style={{ color: colors.red }} />;
    if (type.startsWith('audio/')) return <Music className="w-8 h-8" style={{ color: colors.blue }} />;
    if (type.includes('pdf')) return <FileText className="w-8 h-8" style={{ color: colors.red }} />;
    if (type.includes('document') || type.includes('word')) return <FileText className="w-8 h-8" style={{ color: colors.blue }} />;
    if (type.includes('zip') || type.includes('rar')) return <Archive className="w-8 h-8" style={{ color: colors.yellow }} />;
    return <File className="w-8 h-8" style={{ color: colors.subtext0 }} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, []);

  const handleFiles = (fileList: File[]) => {
    const validFiles: File[] = [];
    const rejectedFiles: File[] = [];

    fileList.forEach(file => {
      if (canUploadFile(file.size)) {
        validFiles.push(file);
      } else {
        rejectedFiles.push(file);
      }
    });

    if (rejectedFiles.length > 0) {
      const rejectedNames = rejectedFiles.map(f => f.name).join(', ');
      alert(`Los siguientes archivos exceden tu límite de almacenamiento: ${rejectedNames}. Actualiza a Premium para almacenamiento ilimitado.`);
    }

    if (validFiles.length > 0) {
      const newFiles: UploadFile[] = validFiles.map(file => ({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0,
        status: 'pending',
        tags: [],
        description: ''
      }));
      
      setFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(file => file.id !== id));
  };

  const updateFile = (id: string, updates: Partial<UploadFile>) => {
    setFiles(prev => prev.map(file => 
      file.id === id ? { ...file, ...updates } : file
    ));
  };

  const addTag = (fileId: string, tag: string) => {
    if (tag.trim()) {
      updateFile(fileId, {
        tags: [...files.find(f => f.id === fileId)?.tags || [], tag.trim()]
      });
    }
  };

  const removeTag = (fileId: string, tagToRemove: string) => {
    const file = files.find(f => f.id === fileId);
    if (file) {
      updateFile(fileId, {
        tags: file.tags.filter(tag => tag !== tagToRemove)
      });
    }
  };

  const simulateUpload = async (file: UploadFile) => {
    updateFile(file.id, { status: 'uploading' });
    
    // Simulate upload progress
    for (let progress = 0; progress <= 100; progress += 10) {
      await new Promise(resolve => setTimeout(resolve, 100));
      updateFile(file.id, { progress });
    }
    
    updateFile(file.id, { status: 'completed', progress: 100 });
    
    // Add to storage usage
    addStorageUsage(file.size);
  };

  const handleUploadAll = async () => {
    setIsUploading(true);
    
    const pendingFiles = files.filter(f => f.status === 'pending');
    
    // Upload files in parallel
    await Promise.all(pendingFiles.map(file => simulateUpload(file)));
    
    setIsUploading(false);
    
    // Navigate back to documents after upload
    setTimeout(() => {
      navigate('/documents');
    }, 1000);
  };

  const canUpload = files.length > 0 && files.some(f => f.status === 'pending');
  const totalSize = files.reduce((total, file) => total + file.size, 0);
  const isPremium = user?.subscription.plan === 'premium';
  const storageUsed = user?.subscription.usage.storageUsed || 0;
  const storageLimit = 500; // 500MB for free users

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
          Upload Documents
        </h1>
        
        <div className="flex items-center gap-2">
          <button
            onClick={handleUploadAll}
            disabled={!canUpload || isUploading}
            className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            style={{ backgroundColor: colors.blue }}
          >
            <Save className="w-4 h-4" />
            {isUploading ? 'Uploading...' : 'Upload All'}
          </button>
          
          <button
            onClick={() => navigate('/documents')}
            className="px-3 py-2 rounded-lg border transition-colors hover:opacity-80"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0,
              color: colors.text
            }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Storage Usage for Free Users */}
      {!isPremium && (
        <div 
          className="p-4 rounded-lg border"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium" style={{ color: colors.text }}>
              Almacenamiento usado
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm" style={{ color: colors.subtext0 }}>
                {storageUsed.toFixed(1)} MB / {storageLimit} MB
              </span>
              <button 
                onClick={() => navigate('/subscription')}
                className="text-xs px-2 py-1 rounded font-medium text-white"
                style={{ backgroundColor: colors.blue }}
              >
                <Crown className="w-3 h-3 inline mr-1" />
                Upgrade
              </button>
            </div>
          </div>
          <div 
            className="w-full h-2 rounded-full"
            style={{ backgroundColor: colors.surface0 }}
          >
            <div 
              className="h-2 rounded-full transition-all"
              style={{ 
                backgroundColor: storageUsed / storageLimit > 0.8 ? colors.red : colors.blue,
                width: `${Math.min((storageUsed / storageLimit) * 100, 100)}%`
              }}
            />
          </div>
          {storageUsed / storageLimit > 0.8 && (
            <div className="flex items-center gap-2 mt-2 text-sm" style={{ color: colors.red }}>
              <AlertTriangle className="w-4 h-4" />
              Espacio de almacenamiento casi agotado
            </div>
          )}
        </div>
      )}

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
          dragActive ? 'border-blue-500 bg-blue-50' : ''
        }`}
        style={{ 
          backgroundColor: dragActive ? colors.blue + '10' : colors.mantle,
          borderColor: dragActive ? colors.blue : colors.surface1
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Upload 
          className="w-16 h-16 mx-auto mb-4" 
          style={{ color: dragActive ? colors.blue : colors.subtext0 }} 
        />
        <h3 className="text-xl font-semibold mb-2" style={{ color: colors.text }}>
          {dragActive ? 'Drop files here' : 'Drag and drop files here'}
        </h3>
        <p className="mb-6" style={{ color: colors.subtext0 }}>
          or click to browse your files
        </p>
        
        <label className="inline-block">
          <input
            type="file"
            multiple
            onChange={handleFileInput}
            className="hidden"
            accept=".pdf,.doc,.docx,.txt,.md,.jpg,.jpeg,.png,.gif,.mp4,.mp3,.zip,.rar"
          />
          <span 
            className="px-6 py-3 rounded-lg font-medium text-white transition-all hover:opacity-90 cursor-pointer inline-block"
            style={{ backgroundColor: colors.blue }}
          >
            Choose Files
          </span>
        </label>
        
        <div className="mt-4 text-sm" style={{ color: colors.subtext0 }}>
          Supported formats: PDF, DOC, TXT, MD, Images, Videos, Audio, Archives
          {!isPremium && (
            <div className="mt-2">
              <span style={{ color: colors.yellow }}>
                Free plan: 500MB total storage limit
              </span>
            </div>
          )}
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div 
          className="rounded-xl border"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <div className="p-4 border-b" style={{ borderColor: colors.surface0 }}>
            <h3 className="text-lg font-semibold" style={{ color: colors.text }}>
              Files to Upload ({files.length})
            </h3>
          </div>
          
          <div className="p-4 space-y-4">
            {files.map((file) => (
              <div
                key={file.id}
                className="p-4 rounded-lg border"
                style={{ 
                  backgroundColor: colors.base,
                  borderColor: colors.surface1
                }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {getFileIcon(file.type)}
                  </div>
                  
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium" style={{ color: colors.text }}>
                          {file.name}
                        </h4>
                        <div className="flex items-center gap-4 text-sm" style={{ color: colors.subtext0 }}>
                          <span>{formatFileSize(file.size)}</span>
                          <span>{file.type || 'Unknown type'}</span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            file.status === 'completed' ? 'bg-green-100 text-green-800' :
                            file.status === 'uploading' ? 'bg-blue-100 text-blue-800' :
                            file.status === 'error' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {file.status}
                          </span>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-1 rounded hover:opacity-80"
                        style={{ color: colors.red }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {/* Progress Bar */}
                    {file.status === 'uploading' && (
                      <div>
                        <div 
                          className="w-full h-2 rounded-full"
                          style={{ backgroundColor: colors.surface0 }}
                        >
                          <div 
                            className="h-2 rounded-full transition-all"
                            style={{ 
                              backgroundColor: colors.blue,
                              width: `${file.progress}%`
                            }}
                          />
                        </div>
                        <div className="text-xs mt-1" style={{ color: colors.subtext0 }}>
                          {file.progress}% uploaded
                        </div>
                      </div>
                    )}
                    
                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: colors.text }}>
                        Description
                      </label>
                      <textarea
                        value={file.description}
                        onChange={(e) => updateFile(file.id, { description: e.target.value })}
                        placeholder="Add a description for this file..."
                        className="w-full px-3 py-2 rounded border resize-none"
                        rows={2}
                        style={{
                          backgroundColor: colors.mantle,
                          borderColor: colors.surface0,
                          color: colors.text
                        }}
                      />
                    </div>
                    
                    {/* Tags */}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                        Tags
                      </label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {file.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs px-2 py-1 rounded flex items-center gap-1"
                            style={{ 
                              backgroundColor: colors.surface0,
                              color: colors.text
                            }}
                          >
                            {tag}
                            <button
                              onClick={() => removeTag(file.id, tag)}
                              className="hover:opacity-80"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Add tag..."
                          className="flex-1 text-sm px-2 py-1 rounded border"
                          style={{
                            backgroundColor: colors.mantle,
                            borderColor: colors.surface1,
                            color: colors.text
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              addTag(file.id, e.currentTarget.value);
                              e.currentTarget.value = '';
                            }
                          }}
                        />
                        <Tag className="w-4 h-4" style={{ color: colors.subtext0 }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Summary */}
      {files.length > 0 && (
        <div 
          className="p-4 rounded-lg border"
          style={{ 
            backgroundColor: colors.surface0,
            borderColor: colors.surface1
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="font-medium" style={{ color: colors.text }}>
                Total: {files.length} files
              </span>
              <span className="ml-4 text-sm" style={{ color: colors.subtext0 }}>
                Size: {formatFileSize(totalSize)}
              </span>
              {!isPremium && (
                <span className="ml-4 text-sm" style={{ 
                  color: (storageUsed + totalSize / (1024 * 1024)) > storageLimit ? colors.red : colors.subtext0 
                }}>
                  After upload: {((storageUsed + totalSize / (1024 * 1024))).toFixed(1)}/500 MB
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-4 text-sm">
              <span style={{ color: colors.green }}>
                ✓ {files.filter(f => f.status === 'completed').length} completed
              </span>
              <span style={{ color: colors.blue }}>
                ↑ {files.filter(f => f.status === 'uploading').length} uploading
              </span>
              <span style={{ color: colors.subtext0 }}>
                ⏳ {files.filter(f => f.status === 'pending').length} pending
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;